import React, { useCallback, useEffect, useState } from 'react';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';

import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';
import { safeLocalStorage } from 'Utilities/storages';

import { Modal } from 'Storybook/components/local/Modal/Modal';
import { RecommendedPhotoTips } from 'Storybook/components/local/RecommendedPhotoTips/RecommendedPhotoTips';

import { useBreakpoint } from '../../../../components/misc/Breakpoints/Breakpoints';
import { tips } from '../shared/DocumentCaptureViewContainer/DocumentCaptureViewContainer';
import { useImageManager } from '../uploadDocuments/hooks/useImageManager';

import LocalTexts from './PhotoTipsModal.texts.json';

import styles from './PhotoTipsModal.scss';

type PhotoTipsModalProps = {
  hasUploadedDocuments: boolean;
}

const LocalT = new MDText(LocalTexts);

export const PHOTO_TIPS_MODAL_KEY = 'photoTipsModalDocs';
export const OVERLAY_TRIGGER_ID = 'overlay-trigger';
export const MODAL_TRIGGER_ID = 'modal-trigger';

export const PhotoTipsModal = ({ hasUploadedDocuments }: PhotoTipsModalProps) => {
  const [openTipsModal, setOpenTipsModal] = useState<string | null>(null);
  const { kind } = useImageManager();

  const breakpoints = useBreakpoint();
  const { breakpointContent } = breakpoints.maxWidth;

  const photoTipsModalDocs = safeLocalStorage.getItem(PHOTO_TIPS_MODAL_KEY) ?? [];
  const modalPreviouslyTriggered = photoTipsModalDocs.includes(kind) || hasUploadedDocuments;

  const openModal = (e: React.MouseEvent) => {
    const modalTriggerId = (e.currentTarget as HTMLButtonElement)?.id;

    setOpenTipsModal(modalTriggerId);
  };

  const closeModal = useCallback(({ isDismissModalClicked = false }: { isDismissModalClicked?: boolean }) => {
    if (isDismissModalClicked) {
      DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.DOCUMENTS_UPLOAD_TIPS_OVERLAY_CLOSE_CLICKED(
        {
          label: kind,
        },
      );
    }

    if (!modalPreviouslyTriggered) {
      // Remove any duplicates using Set
      const kinds = [...new Set([...photoTipsModalDocs, kind])];
      safeLocalStorage.setItem(PHOTO_TIPS_MODAL_KEY, kinds);
    }

    setOpenTipsModal(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeOnAccept = () => {
    const openedByModalTrigger = openTipsModal === MODAL_TRIGGER_ID;

    DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.DOCUMENTS_UPLOAD_TIPS_OVERLAY_BUTTON_CLICKED(
      {
        label: kind,
      },
    );

    // Do not trigger file manager on "Got it" CTA click if modal previously seen or opened by modal trigger
    if (!modalPreviouslyTriggered && !openedByModalTrigger) {
      const input = document.getElementById(`${kind}-0`);

      input?.click();
    }

    closeModal({});
  };

  useEffect(() => {
    if (!breakpointContent) {
      closeModal({});
    }
  }, [breakpointContent, closeModal]);

  return <>
    {/* This overlay appears over the tile(s) to force users to open overlay on first viewing
      * Otherwise we need to dive into a lot of the hook logic which is currently messy
      */}
    {!modalPreviouslyTriggered && <button
      className={styles.overlayTrigger}
      id={OVERLAY_TRIGGER_ID}
      onClick={openModal}
    />}

    <div className={styles.modalTrigger}>
      <Button id={MODAL_TRIGGER_ID} label={LocalT.translate('modalTrigger')} onClick={openModal} size='small' variant='secondary' />
    </div>

    {openTipsModal && <Modal
      className={styles.container}
      contentClass={styles.modalContent}
      dialogClass={styles.modal}
      dismissModal={() => closeModal({ isDismissModalClicked: true })}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      withCloseButton={true}>
      <RecommendedPhotoTips showBorder={false} tips={tips} />
      <div className={styles.buttonContainer}>
        <Button fullWidth label={LocalT.translate('modalConfirm')} onClick={closeOnAccept} />
      </div>
    </Modal>}
  </>;
};
